import React from 'react';
import { FadeInUp, SectionLabel, AccentLine } from './AnimationWrappers';
import { Flame, Wind, Layers, Scissors, Shield, Thermometer } from 'lucide-react';

const ManufacturingSection = () => {
  return (
    <section id="manufacturing" data-testid="manufacturing-section" className="relative overflow-hidden" style={{ background: '#F5F0EB', padding: '70px 0 0' }}>
      <div className="container-main">
        <SectionLabel number="08" title="Infrastructure" />
        <FadeInUp>
          <AccentLine />
          <h2 className="font-display mb-4" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, color: '#1C1C1E' }}>
            Built to <span style={{ color: '#C8102E' }}>Manufacture.</span>
          </h2>
          <p className="font-body mb-4" style={{ fontSize: '1rem', color: '#3A3A3C', maxWidth: '700px', lineHeight: 1.75 }}>
            Our 45,000 sq. ft. purpose-built facility in Tiruppur isn't assembled from off-the-shelf equipment. Every department — from the CNC cutting floor to the seam sealing stations — is designed around the specific demands of technical garment construction. The machinery we invest in exists because the garments we build require it.
          </p>
          <p className="font-body mb-12" style={{ fontSize: '1rem', color: '#3A3A3C', maxWidth: '700px', lineHeight: 1.75 }}>
            Where most factories stop at cut-and-sew, we operate seven specialized departments: automated cutting, bonded fusing, seam sealing, down filling, quilting, and high-precision sewing — each engineered to deliver repeatable, zero-tolerance precision.
          </p>
        </FadeInUp>
      </div>

      {/* === Panoramic Sewing Floor — Full bleed, NO text === */}
      <FadeInUp delay={0.1}>
        <div className="relative w-full overflow-hidden" style={{ height: 'clamp(250px, 35vw, 420px)' }}>
          <img
            src="/mfg-sewing-floor.jpg"
            alt="Production sewing floor — 400+ operators across organized production lines"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 40%' }}
          />
        </div>
      </FadeInUp>

      {/* === Specialized Machinery Section (Dark bg) === */}
      <div style={{ background: 'linear-gradient(165deg, #1A2744 0%, #0F1B2E 40%, #1C1C1E 100%)', padding: '60px 0' }}>
        <div className="container-main">
          <FadeInUp>
            <div className="section-label-light mb-4" style={{ display: 'inline-block' }}>The Machines Behind the Precision</div>
            <div className="accent-line-light" />
            <p className="font-body mb-10" style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.55)', maxWidth: '600px', lineHeight: 1.7 }}>
              Specialized equipment for processes that standard garment factories simply cannot perform. Each machine is a strategic investment in capability.
            </p>
          </FadeInUp>

          {/* Top row: 3 cards */}
          <FadeInUp delay={0.15}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              {/* Seam Sealing */}
              <div className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)',
              }} data-testid="machinery-seam-sealing">
                <div className="relative overflow-hidden" style={{ height: '220px' }}>
                  <img src="/mfg-seam-sealing.jpg" alt="H&H seam sealing machine" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(28,28,30,0.85) 0%, transparent 50%)' }} />
                  <div className="absolute top-3 left-3" style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#C8102E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Shield size={16} color="#fff" strokeWidth={1.5} />
                  </div>
                  <span className="absolute bottom-3 left-4 font-display text-white" style={{ fontSize: '1.2rem', fontWeight: 700 }}>Seam Sealing</span>
                </div>
                <div style={{ padding: '1rem 1.2rem 1.2rem' }}>
                  <p className="font-body mb-3" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                    H&H hot-air seam sealing machines welding PU tape at 420°C. Every seam on every waterproof garment is sealed — no needle hole left exposed.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {['420°C Welding', 'PU Tape', 'H&H Machines'].map((t, i) => (
                      <span key={i} className="font-mono" style={{
                        fontSize: '0.55rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                        padding: '5px 12px', borderRadius: '4px',
                        background: 'rgba(200,16,46,0.15)', color: '#C8102E',
                        border: '1px solid rgba(200,16,46,0.25)',
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Down Filling */}
              <div className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)',
              }} data-testid="machinery-down-filling">
                <div className="relative overflow-hidden" style={{ height: '220px' }}>
                  <img src="/mfg-down-filling.jpg" alt="Richpeace down filling machine" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(28,28,30,0.85) 0%, transparent 50%)' }} />
                  <div className="absolute top-3 left-3" style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#C8102E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Wind size={16} color="#fff" strokeWidth={1.5} />
                  </div>
                  <span className="absolute bottom-3 left-4 font-display text-white" style={{ fontSize: '1.2rem', fontWeight: 700 }}>Down Filling</span>
                </div>
                <div style={{ padding: '1rem 1.2rem 1.2rem' }}>
                  <p className="font-body mb-3" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                    Richpeace automated filling system with gram-accurate distribution. Enclosed chamber prevents fill loss while ensuring uniform insulation density.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Richpeace', 'Gram-Accurate', 'Enclosed Fill'].map((t, i) => (
                      <span key={i} className="font-mono" style={{
                        fontSize: '0.55rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                        padding: '5px 12px', borderRadius: '4px',
                        background: 'rgba(200,16,46,0.15)', color: '#C8102E',
                        border: '1px solid rgba(200,16,46,0.25)',
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bonded Fusing Line */}
              <div className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)',
              }} data-testid="machinery-fusing-lines">
                <div className="relative overflow-hidden" style={{ height: '220px' }}>
                  <img src="/mfg-fusing-lines.jpg" alt="H&H bonded fusing line" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(28,28,30,0.85) 0%, transparent 50%)' }} />
                  <div className="absolute top-3 left-3" style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#C8102E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Thermometer size={16} color="#fff" strokeWidth={1.5} />
                  </div>
                  <span className="absolute bottom-3 left-4 font-display text-white" style={{ fontSize: '1.2rem', fontWeight: 700 }}>Bonded Fusing Line</span>
                </div>
                <div style={{ padding: '1rem 1.2rem 1.2rem' }}>
                  <p className="font-body mb-3" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                    H&H multi-station fusing line with ±2°C temperature control. Sew Free Bonded panel assembly eliminating needle penetration entirely.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {['±2°C Control', 'H&H Line', 'Sew Free Bond'].map((t, i) => (
                      <span key={i} className="font-mono" style={{
                        fontSize: '0.55rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                        padding: '5px 12px', borderRadius: '4px',
                        background: 'rgba(200,16,46,0.15)', color: '#C8102E',
                        border: '1px solid rgba(200,16,46,0.25)',
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FadeInUp>

          {/* Bottom row: Quilting wide + Fusing operators + Auto-cutter + Stats */}
          <FadeInUp delay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">

              {/* Quilting */}
              <div className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1 lg:col-span-2" style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)',
              }} data-testid="machinery-quilting">
                <div className="grid grid-cols-1 md:grid-cols-[0.8fr_1fr]">
                  <div className="relative overflow-hidden" style={{ minHeight: '200px' }}>
                    <img src="/mfg-quilting.jpg" alt="Automated quilting machine" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-col justify-center" style={{ padding: '1.5rem' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#C8102E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Layers size={16} color="#fff" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-display text-white" style={{ fontSize: '1.15rem', fontWeight: 700 }}>Automated Quilting</h3>
                    </div>
                    <p className="font-body mb-3" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                      Specialized quilting machines for baffle-box and stitch-through construction. Programmable patterns ensure uniform stitch density — from lightweight liners to heavy-duty insulated combat garments.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {['Baffle-Box', 'Stitch-Through', 'Programmable'].map((t, i) => (
                        <span key={i} className="font-mono" style={{
                          fontSize: '0.55rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                          padding: '5px 12px', borderRadius: '4px',
                          background: 'rgba(200,16,46,0.15)', color: '#C8102E',
                          border: '1px solid rgba(200,16,46,0.25)',
                        }}>{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fusing Operators */}
              <div className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)',
              }} data-testid="machinery-bonded-fusing">
                <div className="relative overflow-hidden" style={{ height: '180px' }}>
                  <img src="/mfg-bonded-fusing.jpg" alt="Fusing operators at work" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(28,28,30,0.8) 0%, transparent 50%)' }} />
                  <span className="absolute bottom-3 left-4 font-display text-white" style={{ fontSize: '1.05rem', fontWeight: 700 }}>Fusing Operators</span>
                </div>
                <div style={{ padding: '0.8rem 1.2rem 1.2rem' }}>
                  <p className="font-body" style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                    Trained operators at precision heat-press stations handling interlining, reflective transfer, and laminated patch placement.
                  </p>
                </div>
              </div>
            </div>
          </FadeInUp>

          {/* Auto-cutter + Stats */}
          <FadeInUp delay={0.25}>
            <div className="grid grid-cols-1 gap-4 mt-4">
              {/* Auto-cutter */}
              <div className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)',
              }} data-testid="machinery-auto-cutter">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr]">
                  <div className="relative overflow-hidden" style={{ minHeight: '200px' }}>
                    <img src="/mfg-auto-cutter.jpg" alt="CNC auto-cutter" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-col justify-center" style={{ padding: '1.5rem' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#C8102E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Scissors size={16} color="#fff" strokeWidth={1.5} />
                      </div>
                      <h3 className="font-display text-white" style={{ fontSize: '1.1rem', fontWeight: 700 }}>CNC Auto-Cutter</h3>
                    </div>
                    <p className="font-body mb-3" style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                      Computer-controlled multi-ply cutting with ±0.1mm tolerance. Automated pattern nesting maximizes fabric utilization at 95%.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {['±0.1mm', 'Multi-Ply', 'Nesting'].map((t, i) => (
                        <span key={i} className="font-mono" style={{
                          fontSize: '0.55rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                          padding: '5px 12px', borderRadius: '4px',
                          background: 'rgba(200,16,46,0.15)', color: '#C8102E',
                          border: '1px solid rgba(200,16,46,0.25)',
                        }}>{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </div>
    </section>
  );
};

export default ManufacturingSection;
